/**
 * Minimum length a numeric sequence must reach to be treated as an OTP code
 * when the caller does not know the exact expected length.
 */
export const MIN_OTP_LENGTH = 4;

/**
 * Extracts a numeric OTP code from arbitrary text pasted from the clipboard,
 * for example the full body of an SMS or email such as "Tu código es 123456".
 *
 * - Ignores surrounding text, spaces, line breaks and special characters.
 * - When `expectedLength` is provided, only returns a code matching that length
 *   (either a single numeric group or every digit joined, e.g. "123 456").
 * - When `expectedLength` is not provided, returns the longest numeric sequence
 *   as long as it reaches MIN_OTP_LENGTH digits (so text like "ABC123" or a
 *   partial code is rejected and manual entry is preserved).
 *
 * @param {string} text - Raw pasted text.
 * @param {number} [expectedLength] - Exact number of digits the OTP should have.
 * @returns {string|null} The extracted code, or null when no valid code is found.
 */
export const extractOtpFromText = (text, expectedLength) => {
  if (!text) return null;

  const digitGroups = text.match(/\d+/g);
  if (!digitGroups) return null;

  if (expectedLength) {
    const exactGroup = digitGroups.find((group) => group.length === expectedLength);
    if (exactGroup) return exactGroup;

    const allDigits = digitGroups.join('');
    return allDigits.length === expectedLength ? allDigits : null;
  }

  const longestGroup = digitGroups.reduce(
    (longest, current) => (current.length > longest.length ? current : longest),
    ''
  );

  return longestGroup.length >= MIN_OTP_LENGTH ? longestGroup : null;
};
